import { fullName } from 'module/helpers/full-name';
import { initials } from 'module/helpers/initials';
import { Dropdown } from 'module/ui/components/atoms/dropdown/dropdown';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { Tag } from 'module/ui/components/atoms/tag';
import { Heading02, Heading07, Short01 } from 'module/ui/components/styles';
import { faLinkedin } from '@fortawesome/free-brands-svg-icons';
import { faEllipsis, faLink } from '@fortawesome/pro-regular-svg-icons';
import { Button } from '@rocketlane/ui';
import { Image as AntImage } from 'antd';
import { PeopleListRow } from 'api/account-people/types';
import { SignalType } from 'api/signal/types';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import { signalTypeConfigs } from 'components/organisms/signal/signal-type/type-configs';
import { useAccountPeopleRouteNavigation } from '../route-navigation';

const Image = styled(AntImage)``;
const AvatarWrapper = styled.div`
  width: 100px;
  height: 100px;
  border-radius: var(--border-radius-default);
  background: var(--gray-gradient);
  display: flex;
  align-items: center;
  justify-content: center;
  ${Heading07};
  color: var(--white-100);
  font-size: 54px;
  overflow: hidden;
  ${Image} {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const UserProfileImage = ({
  user,
  className,
}: {
  user: PeopleListRow['user'];
  className?: string;
}) => {
  const [errored, setErrored] = useState(false);
  const fullname = fullName(user);
  const userInitials = initials(fullname);

  return (
    <AvatarWrapper className={className}>
      {user.photoUrl != null && !errored ? (
        <Image
          loading="lazy"
          alt={user.firstName ?? user.lastName}
          src={user.photoUrl}
          crossOrigin="anonymous"
          preview={false}
          onError={() => setErrored(true)}
        />
      ) : (
        <>{userInitials}</>
      )}
    </AvatarWrapper>
  );
};
const Name = styled.div`
  ${Heading02};
`;
const Role = styled.div`
  ${Short01};
  color: var(--text-secondary);
`;
const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-02);
`;
const Actions = styled.div`
  display: flex;
  gap: var(--spacing-03);
  position: absolute;
  right: 0;
  top: 0;
  .rl-btn svg {
    color: var(--icon-secondary);
  }
  visibility: hidden;
`;
const Content = styled.div`
  padding: var(--spacing-02) 0;
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;
const ProfileImage = styled(UserProfileImage)`
  flex-shrink: 0;
`;

const Wrapper = styled.div<{ moreActionsOpen: boolean }>`
  border: 1px solid var(--field-border-default);
  border-radius: var(--border-radius-small);
  box-shadow: var(--shadow-02);
  padding: var(--spacing-02);
  display: flex;
  gap: var(--spacing-04);
  cursor: pointer;
  ${({ moreActionsOpen }) =>
    moreActionsOpen &&
    css`
      ${Actions} {
        visibility: visible;
      }
    `}
  &:hover {
    ${Actions} {
      visibility: visible;
    }
  }
`;

export const AccountPeopleBoardUserCard = ({
  data,
}: {
  data: PeopleListRow;
}) => {
  const copyCurrentUrlToClipboard = () => {
    if (typeof window !== 'undefined' && window.location) {
      navigator.clipboard.writeText(window.location.href);
    }
  };
  const linkedInProfile = data.userProfile?.enrichedUserData?.linkedInProfile;
  const [moreActionsOpen, setMoreActionsOpen] = useState(false);
  const { navigateToPeopleDetail } = useAccountPeopleRouteNavigation();
  const latestRiskSignal = data.recentRiskSignals?.[0];

  return (
    <Wrapper
      moreActionsOpen={moreActionsOpen}
      onClick={() => {
        navigateToPeopleDetail(data.user.company.companyId, data.user.userId);
      }}
    >
      <ProfileImage user={data.user} />
      <Content>
        <UserInfo>
          <Name>{fullName(data.user)}</Name>
          {data.user.role?.roleName && <Role>{data.user.role?.roleName}</Role>}
        </UserInfo>
        {latestRiskSignal && (
          <div>
            <Tag
              color={signalTypeConfigs[SignalType.Risk].color}
              icon={
                <FontAwesomeIcon
                  icon={signalTypeConfigs[SignalType.Risk].rawIcon}
                  size={12}
                />
              }
            >
              {latestRiskSignal?.signalName}
            </Tag>
          </div>
        )}
        <Actions>
          {linkedInProfile && (
            <Button
              variant="ghost"
              size="small"
              leftIcon={<FontAwesomeIcon icon={faLinkedin} size={12} />}
              onClick={(e) => {
                e.stopPropagation();
                window.open(linkedInProfile, '_blank', 'noopener,noreferrer');
              }}
            />
          )}
          <div onClick={(e) => e.stopPropagation()}>
            <Dropdown
              trigger={['click']}
              onOpenChange={(open) => setMoreActionsOpen(open)}
              menu={{
                onClick: (e) => {
                  if (e.key === 'copy_url') {
                    copyCurrentUrlToClipboard();
                  }
                },
                items: [
                  {
                    type: 'group',
                    label: <FormattedMessage id="more" />,
                    children: [
                      {
                        key: 'copy_url',
                        label: <FormattedMessage id="copy_url" />,
                        icon: <FontAwesomeIcon icon={faLink} />,
                      },
                    ],
                  },
                ],
              }}
            >
              <Button
                leftIcon={<FontAwesomeIcon icon={faEllipsis} size={12} />}
                variant="ghost"
                size="small"
              />
            </Dropdown>
          </div>
        </Actions>
      </Content>
    </Wrapper>
  );
};
